/**
 * Confirmation shared by every screen that duplicates a Média content (written by
 * the admin / community-manager accounts) into the active client domain.
 *
 * When the domain already received the content, the dialog says so and the request
 * carries the `confirm_already_duplicated` flag the API requires before creating a
 * second copy. The caller keeps its own wording for a first duplication.
 */
export const ALREADY_DUPLICATED_PAYLOAD = { confirm_already_duplicated: true }

export function isDuplicatedByDomain (duplicatedBy, domainUuid) {
  return Boolean(domainUuid) && Array.isArray(duplicatedBy) && duplicatedBy.includes(domainUuid)
}

const COPY_STATUS_LABELS = {
  draft: 'Brouillon',
  scheduled: 'Programmé',
  published: 'Publié',
  disabled: 'Désactivé',
  ended: 'Terminé'
}

function formatCopyDate (value) {
  if (!value) return null
  const date = new Date(String(value).replace(' ', 'T'))
  if (Number.isNaN(date.getTime())) return null
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} à ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/**
 * Describes the copy the domain already holds, when the API knows it. Older
 * duplications (flag only, no recorded copy) get the generic sentence.
 *
 * @param {{ publication_date?: string, status?: string } | null} localCopy
 */
function localCopyDetails (localCopy) {
  if (!localCopy) return ''
  if (localCopy.status === 'deleted') {
    const duplicatedAt = formatCopyDate(localCopy.duplicated_at)
    const deletedAt = formatCopyDate(localCopy.deleted_at)
    const state = COPY_STATUS_LABELS[localCopy.state_at_deletion] || null
    const notified = localCopy.notified_at_deletion
    const details = [
      duplicatedAt ? `dupliquée le <strong>${duplicatedAt}</strong>` : null,
      deletedAt ? `supprimée le <strong>${deletedAt}</strong>` : null,
      state ? `elle était alors <strong>${state.toLowerCase()}</strong>` : null,
      // Only known for copies deleted since the trace records it.
      notified === true ? 'notification <strong>envoyée</strong>' : null,
      notified === false ? 'notification <strong>non envoyée</strong>' : null
    ].filter(Boolean)
    return '<br>La copie a été <strong>supprimée</strong> depuis' +
      (details.length ? ` (${details.join(', ')}).` : '.')
  }
  const date = formatCopyDate(localCopy.publication_date)
  const status = COPY_STATUS_LABELS[localCopy.status] || localCopy.status
  const parts = [
    date ? `date de publication : <strong>${date}</strong>` : 'sans date de publication',
    status ? `statut actuel : <strong>${status}</strong>` : null
  ].filter(Boolean)
  return `<br>Copie existante — ${parts.join(', ')}.`
}

export function alreadyDuplicatedMessage (contentLabel = 'ce contenu', localCopy = null) {
  return `Vous avez déjà dupliqué ${contentLabel} sur votre domaine.` +
    localCopyDetails(localCopy) +
    '<br><br>Une nouvelle copie sera créée. Voulez-vous continuer ?'
}

/**
 * @param {Vue} vm component instance (needs `$buefy`)
 * @param {object} options
 * @param {boolean} options.alreadyDuplicated the active domain already received the content
 * @param {string} options.title title of the first-duplication dialog
 * @param {string} options.message message of the first-duplication dialog (HTML allowed)
 * @param {string} [options.confirmText]
 * @param {string} [options.type] buefy type of the first-duplication dialog
 * @param {string} [options.contentLabel] "cette actualité", "ce sondage"…
 * @param {object|null} [options.localCopy] `local_copy` of the list item: date and status of the copy already in the domain
 * @param {(payload: object) => void} options.onConfirm receives the body to send with the POST
 */
export function confirmMediaDuplication (vm, {
  alreadyDuplicated,
  title,
  message,
  confirmText = 'Dupliquer',
  type = 'is-info',
  contentLabel = 'ce contenu',
  localCopy = null,
  onConfirm
}) {
  if (!alreadyDuplicated) {
    vm.$buefy.dialog.confirm({
      title,
      message,
      confirmText,
      cancelText: 'Annuler',
      type,
      onConfirm: () => onConfirm({})
    })
    return
  }

  vm.$buefy.dialog.confirm({
    title: 'Contenu déjà dupliqué',
    message: alreadyDuplicatedMessage(contentLabel, localCopy),
    confirmText: 'Dupliquer à nouveau',
    cancelText: 'Annuler',
    type: 'is-warning',
    hasIcon: true,
    onConfirm: () => onConfirm({ ...ALREADY_DUPLICATED_PAYLOAD })
  })
}
